"use client";

import React from "react";
import { DesignSystemFixtureMetadata } from "@features/design-system/design-system-types";
import { FixtureInvariantMeta } from "@features/design-system/ui/fixture/fixture-invariant-meta";
import { FixtureLibsUsed } from "@features/design-system/ui/fixture/fixture-libs-used";
import { FixtureMetadata } from "@features/design-system/ui/fixture/fixture-metadata";
import { FixturePropertyImpactTable } from "@features/design-system/ui/fixture/fixture-property-impact-table";
import {
  FixturePropsTable,
  PropertyDefinition,
} from "@features/design-system/ui/fixture/fixture-props-table";
import { FixtureSection } from "@features/design-system/ui/fixture/fixture-section";
import { FixtureWrapper } from "@features/design-system/ui/fixture/fixture-wrapper";
import { SpeakerAreaProposalEntity } from "@features/proposals/types/proposal";
import { ProposalCard } from "@features/proposals/ui/proposal-card";

// 1) Документация свойств публичного API
const generalProps: PropertyDefinition[] = [
  {
    name: "proposal",
    type: "SpeakerAreaProposalEntity",
    defaultValue: "undefined",
    description:
      "Доменная сущность заявки. Используются поля activity.typeValue, activity.scope, activity.content (включая OG-изображения), activity.content.data.title, member.role, event.content.data.{title,siteUri,logoUri.iv}, event.hint, workflow.status, а также entity.uiLinks.edit и rights.canPatch для ссылки действия.",
  },
  {
    name: "className",
    type: "string",
    defaultValue: "undefined",
    description: "Дополнительные CSS‑классы для контейнера карточки.",
  },
];
// 2) Утилиты и генераторы тестовых данных
// Хелпер для локализуемых полей (LocaleMessageType)
const R = (value: string) => ({ ru: value, en: value });

const base: SpeakerAreaProposalEntity = {
  id: 1,
  uiLinks: { edit: "/proposals/proposal-1/edit" },
  rights: { canPatch: true },
  activity: {
    id: 1,
    typeValue: "TALK",
    scope: "REGULAR",
    content: { data: { title: R("Введение в архитектуру микросервисов") } },
    rights: { canPatch: true },
  },
  member: {
    id: 1,
    role: "SPEAKER",
    rights: { canPatch: true },
  },
  event: {
    id: 1,
    hint: "JPoint",
    content: {
      data: {
        title: R("JPoint 2025"),
        siteUri: R("https://jpoint.ru"),
        logoUri: { iv: "https://placehold.co/100x100?text=Logo" },
      },
    },
  },
  workflow: { status: "new" },
};

const withOGImage: SpeakerAreaProposalEntity = {
  ...base,
  id: 2,
  // @ts-expect-error: types
  activity: {
    ...base.activity,
    content: {
      data: {
        ...base.activity?.content?.data,
        OGImage: { ru: ["og-ru-id"], en: ["og-en-id"] },
      },
      assets: [
        {
          id: "og-ru-id",
          links: { content: "https://placehold.co/800x600/1a56db/white?text=OG+RU" },
        },
        {
          id: "og-en-id",
          links: { content: "https://placehold.co/800x600/dc2626/white?text=OG+EN" },
        },
      ],
    },
  },
};

const withNoLogo: SpeakerAreaProposalEntity = {
  ...base,
  id: 3,
  // @ts-expect-error: types
  event: {
    ...base.event,
    content: { data: { title: R("Joker 2025"), siteUri: R("https://jokerconf.com") } },
  },
};

const withOGNoLogo: SpeakerAreaProposalEntity = {
  ...base,
  id: 4,
  // @ts-expect-error: types
  activity: {
    ...base.activity,
    content: {
      data: {
        ...base.activity?.content?.data,
        OGImage: { ru: ["og-no-logo-id"], en: ["og-no-logo-id"] },
      },
      assets: [
        {
          id: "og-no-logo-id",
          links: { content: "https://placehold.co/800x600/059669/white?text=OG+Image" },
        },
      ],
    },
  },
  // @ts-expect-error: types
  event: {
    ...base.event,
    content: { data: { title: R("HolyJS 2025"), siteUri: R("https://holyjs.ru") } },
  },
};

const withOGOnlyRu: SpeakerAreaProposalEntity = {
  ...base,
  id: 5,
  // @ts-expect-error: types
  activity: {
    ...base.activity,
    content: {
      data: {
        ...base.activity?.content?.data,
        OGImage: { ru: ["og-only-ru-id"], en: [] },
      },
      assets: [
        {
          id: "og-only-ru-id",
          links: { content: "https://placehold.co/800x600/7c3aed/white?text=OG+Only+RU" },
        },
      ],
    },
  },
};

const withSpecialScope: SpeakerAreaProposalEntity = {
  ...base,
  id: 6,
  // @ts-expect-error: types
  activity: { ...base.activity, scope: "PARTNER" },
  // @ts-expect-error: types
  member: { ...base.member, role: "EXPERT" },
};

const openProposal: SpeakerAreaProposalEntity = {
  ...base,
  id: 7,
  workflow: { status: "consideration" },
};
const closedRejected: SpeakerAreaProposalEntity = {
  ...base,
  id: 8,
  workflow: { status: "rejected" },
};
const accepted: SpeakerAreaProposalEntity = {
  ...base,
  id: 9,
  workflow: { status: "accepted" },
  // @ts-expect-error: types
  member: { ...base.member, role: "HOST" },
};

const noTitle: SpeakerAreaProposalEntity = {
  ...base,
  id: 10,
  // @ts-expect-error: types
  activity: { ...base.activity, content: { data: {} } },
};

const ProposalCardFixture = () => (
  <>
    {/* 1. Описание */}
    <FixtureSection title="1. Описание" id="about">
      <div className="space-y-4">
        <p>
          Карточка заявки <strong>ProposalCard</strong> отображает ключевую информацию о докладе:
          локализованный заголовок, тип активности, роль участника, связанное событие, текущий
          статус workflow и доступное действие. Компонент аккуратно деградирует при неполных данных
          и использует защитный рендеринг.
        </p>
        <p>
          Когда использовать: в списках заявок спикера, в карточках на дашбордах программы, на
          страницах события для быстрого перехода к редактированию или просмотру.
        </p>
        <p>
          <strong>Ключевые особенности:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Локализация заголовка и текстов статусов через i18n‑хуки.</li>
          <li>Отображение роли участника (SPEAKER/EXPERT/HOST) с подсказкой при наведении.</li>
          <li>
            Приоритетная загрузка OG-изображений активности для текущей локали с автоматическим
            фоллбэком на логотип события.
          </li>
          <li>Контейнерные запросы Tailwind для адаптивной типографики внутри карточки.</li>
          <li>Дефенсивный рендеринг: отсутствие заголовка скрывает карточку как недоступную.</li>
          <li>Автовыбор режима действия: «edit» или «view» по правилу закрытости заявки.</li>
          <li>Аккуратная обработка брендинга события и ссылок на сайт события.</li>
        </ul>
      </div>
    </FixtureSection>

    {/* 2. Meta */}
    <FixtureSection title="2. Meta" id="meta">
      <FixtureMetadata
        componentName="ProposalCard"
        sourcePath="src/features/proposals/ui/proposal-card.tsx"
        domain="Proposals (управление заявками спикеров)"
        dependencies={[
          {
            path: "@features/proposals/hooks/proposal-translate-fields",
            description: "локализация полей заявки",
          },
          {
            path: "@features/proposals/proposals-tools",
            description: "утилиты для работы со статусами",
          },
          {
            path: "@components/ui/card",
            description: "UI‑примитивы карточки (shadcn/ui)",
          },
          {
            path: "@components/ui/custom/fade-failsafe-image",
            description: "защищённая загрузка изображений",
          },
          {
            path: "@features/events/ui/event-site-link",
            description: "ссылка на сайт события",
          },
          {
            path: "@features/proposals/ui/member/member-role-badge",
            description: "бейдж роли участника активности",
          },
          {
            path: "@components/common/buttons/entity-edit-or-view-link",
            description: "универсальная ссылка действия",
          },
        ]}
      />
    </FixtureSection>

    {/* 3. Бизнес‑контекст */}
    <FixtureSection title="3. Бизнес‑контекст" id="business-context">
      <div className="space-y-4">
        <p>
          <strong>Роли и сценарии использования:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Спикер:</strong> просмотр поданных заявок в личном кабинете. Ожидаемая ценность:
            быстрый обзор статуса заявки, связь с событием и доступные действия (редактирование или
            просмотр).
          </li>
          <li>
            <strong>Организатор события:</strong> обзор заявок по нескольким событиям. Ожидаемая
            ценность: визуальная идентификация через брендинг события и отслеживание статусов.
          </li>
          <li>
            <strong>Программный комитет:</strong> оценка очереди заявок. Ожидаемая ценность: быстрая
            фильтрация по статусам и scope, доступ к редактированию/просмотру деталей.
          </li>
          <li>
            <strong>Контент‑менеджер:</strong> мониторинг CFP‑воронки. Ожидаемая ценность:
            визуализация статусов и отслеживание прогресса по workflow.
          </li>
        </ul>
        <p>
          <strong>Ключевые ограничения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Компонент рендерит <code>null</code>, если отсутствует{" "}
            <code>activity.content.data.title</code> (защитное поведение).
          </li>
          <li>
            Режим действия переключается автоматически на основе бизнес‑правила{" "}
            <code>isClosedProposal()</code>.
          </li>
          <li>Брендинг события (логотип) опционален и корректно деградирует при отсутствии.</li>
          <li>
            Весь текстовый контент локализован через <code>next-intl</code> и доменные хуки
            перевода.
          </li>
          <li>
            Навигация использует Next.js App Router <code>Link</code> компонент.
          </li>
          <li>
            Параметр возврата в URL явно отключён для предотвращения засорения истории навигации.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 4. Пропсы и типы */}
    <FixtureSection title="4. Пропсы и типы" id="props-types">
      <FixturePropsTable properties={generalProps} />
      <FixturePropertyImpactTable
        properties={[
          {
            propertyPath: "proposal.activity.content.data.title",
            type: "LocaleMessageType",
            renderImpact: "Основной заголовок карточки",
            missingBehavior: "Возвращает null, карточка не отображается",
          },
          {
            propertyPath: "proposal.activity.typeValue",
            type: "string",
            renderImpact: "Описание карточки (локализовано)",
            missingBehavior: "Показывает пустую строку",
          },
          {
            propertyPath: "proposal.member.role",
            type: "'SPEAKER' | 'EXPERT' | 'HOST' | undefined",
            renderImpact: "Бейдж роли участника с подсказкой",
            missingBehavior: "Бейдж не отображается",
          },
          {
            propertyPath: "proposal.activity.scope",
            type: "'REGULAR' | 'MEETUP' | 'PARTNER' | 'COMMITTEE'",
            renderImpact: "Управляет видимостью бейджа",
            missingBehavior: "Бейдж скрыт при REGULAR",
          },
          {
            propertyPath: "proposal.activity.content.data.OGImage",
            type: "LocaleMessageTypeArray (массивы ID ассетов)",
            renderImpact: "Приоритетное изображение заголовка карточки для текущей локали",
            missingBehavior: "Фоллбэк на event.content.data.logoUri.iv",
          },
          {
            propertyPath: "proposal.event.content.data.logoUri.iv",
            type: "string | undefined",
            renderImpact: "Изображение брендинга события (фоллбэк)",
            missingBehavior: "Корректно пропускается, фон остается без изображения",
          },
          {
            propertyPath: "proposal.event.content.data.siteUri",
            type: "LocaleMessageType",
            renderImpact: "Ссылка на сайт события",
            missingBehavior: "Компонент Link обрабатывает отсутствие",
          },
          {
            propertyPath: "proposal.workflow.status",
            type: "ProposalWorkflowStatus",
            renderImpact: "Бейдж статуса и описание",
            missingBehavior: "Пустое состояние",
          },
          {
            propertyPath: "proposal.uiLinks.edit",
            type: "string",
            renderImpact: "Целевая ссылка действия",
            missingBehavior: "Требуется для навигации",
          },
        ]}
      />
    </FixtureSection>

    {/* 5. Инварианты */}
    <FixtureSection title="5. Инварианты" id="invariants">
      {/* 5.1 Базовый */}
      <FixtureSection title="5.1 Базовый" id="invariant-base-render" variant="inner">
        <FixtureInvariantMeta
          id="base-render"
          name="Базовая карточка заявки с логотипом события"
          goal="Проверка стандартного рендеринга со всеми заполненными данными и логотипом события."
          preconditions="Все обязательные и опциональные поля заполнены корректно, OG-изображения отсутствуют."
          inputData={[
            'activity.typeValue = "TALK"',
            'activity.scope = "REGULAR"',
            "activity.content.data.title = локализованная строка",
            'member.role = "SPEAKER"',
            "activity.content.data.OGImage отсутствует",
            "event.content.data.logoUri.iv = валидный URL",
            'workflow.status = "new" (не закрыта)',
          ]}
          expectedResult='Карточка отображает заголовок, тип активности, бейдж роли SPEAKER, центрированный логотип события на фоне с паттерном, статус "new" и кнопку "Редактировать".'
          edgeCases="Нет."
        />
        <FixturePropsTable
          properties={[
            {
              name: "proposal.activity.typeValue",
              type: "string",
              value: '"TALK"',
              description: "Тип активности",
            },
            {
              name: "proposal.member.role",
              type: "ActivityMemberRoleType",
              value: '"SPEAKER"',
              description: "Роль участника",
            },
            {
              name: "proposal.activity.scope",
              type: "'REGULAR' | 'MEETUP' | 'PARTNER' | 'COMMITTEE'",
              value: "'REGULAR'",
              description: "Обычный охват",
            },
            {
              name: "proposal.activity.content.data.title",
              type: "LocaleMessageType",
              value: '{ ru: "Введение в архитектуру микросервисов" }',
              description: "Локализованный заголовок",
            },
            {
              name: "proposal.event.content.data.logoUri.iv",
              type: "string",
              value: "URL",
              description: "Логотип события",
            },
            {
              name: "proposal.workflow.status",
              type: "ProposalWorkflowStatus",
              value: '"new"',
              description: "Новый (не закрыто)",
            },
          ]}
        />
        <FixtureWrapper label="Базовый" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <ProposalCard proposal={base} />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.2 С OG-изображением */}
      <FixtureSection title="5.2 С OG-изображением" id="invariant-og-image" variant="inner">
        <FixtureInvariantMeta
          id="og-image"
          name="Приоритет OG-изображения над логотипом"
          goal="Проверка приоритетной загрузки OG-изображения для текущей локали."
          preconditions="OG-изображения заданы для обеих локалей, логотип события также присутствует."
          inputData={[
            "activity.content.data.OGImage = { ru: ['og-ru-id'], en: ['og-en-id'] }",
            "activity.content.assets содержит соответствующие изображения",
            "event.content.data.logoUri.iv = валидный URL (игнорируется)",
          ]}
          expectedResult="Карточка отображает OG-изображение для текущей локали (ru или en) с абсолютным позиционированием, растянутое на весь контейнер (object-cover). Логотип события не отображается. Фоновый паттерн и цвет контейнера скрыты."
          edgeCases="При переключении языка должно меняться изображение (ru ↔ en)."
        />
        <FixturePropsTable
          properties={[
            {
              name: "proposal.activity.content.data.OGImage",
              type: "LocaleMessageTypeArray",
              value: "{ ru: ['og-ru-id'], en: ['og-en-id'] }",
              description: "OG-изображения для обеих локалей",
            },
            {
              name: "proposal.event.content.data.logoUri.iv",
              type: "string",
              value: "URL логотипа",
              description: "Присутствует, но игнорируется из-за приоритета OG",
            },
          ]}
        />
        <FixtureWrapper label="С OG-изображением (и логотипом)" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <ProposalCard proposal={withOGImage} />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.3 Различные состояния изображений */}
      <FixtureSection
        title="5.3 Различные состояния изображений"
        id="invariant-image-states"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="image-states"
          name="Галерея состояний изображений"
          goal="Проверка корректного отображения различных комбинаций OG-изображений и логотипов."
          preconditions="Нет."
          inputData={[
            "Состояние 1: Только логотип события (без OG)",
            "Состояние 2: OG + логотип события (OG приоритетнее)",
            "Состояние 3: Только OG, без логотипа",
            "Состояние 4: OG только для ru, для en — фоллбэк на логотип",
            "Состояние 5: Ни OG, ни логотипа (пустой фон с паттерном)",
          ]}
          expectedResult="Каждое состояние корректно рендерится согласно приоритету: OG текущей локали → логотип события → пустой фон."
          edgeCases="При отсутствии OG для текущей локали должен использоваться логотип события."
        />
        <FixtureWrapper label="Состояния изображений" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {/* Только логотип */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">1. Только логотип</div>
              <ProposalCard proposal={base} />
            </div>

            {/* OG + логотип */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                2. OG (приоритет) + логотип
              </div>
              <ProposalCard proposal={withOGImage} />
            </div>

            {/* Только OG */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                3. Только OG, без логотипа
              </div>
              <ProposalCard proposal={withOGNoLogo} />
            </div>

            {/* OG только ru */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                4. OG только RU (для EN → логотип)
              </div>
              <ProposalCard proposal={withOGOnlyRu} />
            </div>

            {/* Ни OG ни логотипа */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                5. Без OG и логотипа (фон)
              </div>
              <ProposalCard proposal={withNoLogo} />
            </div>
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.4 Нестандартный scope */}
      <FixtureSection title="5.4 Нестандартный scope" id="invariant-special-scope" variant="inner">
        <FixtureInvariantMeta
          id="special-scope"
          name="Нестандартный охват (scope)"
          goal="Проверка отображения бейджа видимости для особых типов scope."
          preconditions='activity.scope не равен "REGULAR".'
          inputData={[
            'activity.scope = "PARTNER" (или "MEETUP", "COMMITTEE")',
            'member.role = "EXPERT"',
          ]}
          expectedResult="В блоке CardAction отображается ActivityScopeBadge с соответствующей меткой. Рядом с типом активности отображается бейдж роли EXPERT."
          edgeCases='При scope = "REGULAR" бейдж не отображается.'
        />
        <FixturePropsTable
          properties={[
            {
              name: "proposal.activity.scope",
              type: "'REGULAR' | 'MEETUP' | 'PARTNER' | 'COMMITTEE'",
              value: "'PARTNER'",
              description: "Нестандартный охват (пример)",
            },
            {
              name: "proposal.member.role",
              type: "ActivityMemberRoleType",
              value: "'EXPERT'",
              description: "Роль эксперта",
            },
          ]}
        />
        <FixtureWrapper label="Особый scope" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <ProposalCard proposal={withSpecialScope} />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.5 Открытая vs закрытая заявка */}
      <FixtureSection
        title="5.5 Открытая vs закрытая заявка"
        id="invariant-open-closed"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="open-closed-proposal"
          name="Режим редактирования vs просмотра"
          goal="Проверка автоматического переключения режима действия в зависимости от статуса заявки."
          preconditions="Статус workflow влияет на результат isClosedProposal()."
          inputData={[
            'Открытая: workflow.status = "consideration" → режим "edit"',
            'Закрытая: workflow.status = "rejected" → режим "view"',
          ]}
          expectedResult='Для открытой заявки кнопка внизу — "Редактировать", для закрытой — "Просмотр" с соответствующей иконкой.'
          edgeCases='Все закрытые статусы (rejected, recalled, duplicated, closed) должны переключать режим на "view".'
        />
        <FixturePropsTable
          properties={[
            {
              name: "open.workflow.status",
              type: "ProposalWorkflowStatus",
              value: "'consideration'",
              description: "Считается не закрытой",
            },
            {
              name: "closed.workflow.status",
              type: "ProposalWorkflowStatus",
              value: "'rejected'",
              description: "Считается закрытой",
            },
            {
              name: "accepted.member.role",
              type: "ActivityMemberRoleType",
              value: "'HOST'",
              description: "Роль ведущего (в примере accepted)",
            },
          ]}
        />
        <FixtureWrapper label="Открытая и закрытая" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <ProposalCard proposal={openProposal} />
            <ProposalCard proposal={closedRejected} />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.6 Галерея статусов */}
      <FixtureSection
        title="5.6 Галерея статусов"
        id="invariant-all-workflow-statuses"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="all-workflow-statuses"
          name="Галерея всех статусов workflow"
          goal="Визуальная регрессия для всех возможных статусов ProposalWorkflowStatus."
          preconditions="Нет."
          inputData={[
            "Набор заявок с различными статусами (new, working_out, consideration, rejected, accepted и др.)",
          ]}
          expectedResult="Каждая карточка корректно отображает соответствующий бейдж статуса и описание."
          edgeCases="Проверка всех 11 статусов на консистентность стилей и локализации."
        />
        <FixtureWrapper label="Статусы" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            <ProposalCard proposal={base} />
            <ProposalCard proposal={openProposal} />
            <ProposalCard proposal={accepted} />
            <ProposalCard proposal={closedRejected} />
          </div>
        </FixtureWrapper>
      </FixtureSection>

      {/* 5.7 Отсутствует заголовок (инвариант невидимости) */}
      <FixtureSection
        title="5.7 Отсутствует заголовок"
        id="invariant-missing-title"
        variant="inner"
      >
        <FixtureInvariantMeta
          id="missing-title"
          name="Отсутствие заголовка активности"
          goal="Подтверждение защитного рендеринга при критически важном отсутствующем поле."
          preconditions="activity.content.data.title = undefined."
          inputData={["activity.content.data = {} (пустой объект без title)"]}
          expectedResult="Компонент возвращает null, карточка не занимает место в DOM."
          edgeCases="Это критическое бизнес‑правило — без заголовка карточка считается недоступной."
        />
        <FixtureWrapper label="Нет заголовка — нет карточки" variant="default">
          <div className="grid w-full auto-rows-min grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {/* Ничего не будет выведено: */}
            <ProposalCard proposal={noTitle} />
            {/* Маркер пустого места: */}
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              Ожидаемо пусто: карточка не рендерится без заголовка.
            </div>
          </div>
        </FixtureWrapper>
      </FixtureSection>
    </FixtureSection>

    {/* 6. Примеры и edge-cases */}
    <FixtureSection title="6. Примеры и крайние случаи" id="edge-cases">
      <div className="space-y-4">
        <p>
          <strong>Полнота данных:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            <strong>Полные данные:</strong> Все поля заполнены → полноценный рендер с брендингом.
          </li>
          <li>
            <strong>Минимальные данные:</strong> Только обязательные поля → карточка рендерится без
            опциональных элементов.
          </li>
          <li>
            <strong>Отсутствует заголовок:</strong> activity.content.data.title = undefined →
            компонент возвращает null.
          </li>
          <li>
            <strong>Очень длинный заголовок:</strong> 200+ символов → адаптивная типография, перенос
            текста.
          </li>
        </ul>
        <p>
          ProposalWorkflowStatus: new, working_out, consideration, rejected, recalled, reserved,
          duplicated, accepted, analysing, completed, closed.
        </p>
      </div>
    </FixtureSection>

    {/* 7. Accessibility */}
    <FixtureSection title="7. Доступность" id="accessibility">
      <ul className="ml-6 list-disc space-y-1">
        <li>Ссылки доступны с клавиатуры (Tab, Enter/Space).</li>
        <li>Атрибут alt логотипа использует event.hint.</li>
        <li>Фоновые паттерны помечены aria-hidden.</li>
        <li>Контрастность соответствует WCAG AA.</li>
      </ul>
    </FixtureSection>

    {/* 8. FixtureLibsUsed */}
    <FixtureSection title="8. Используемые библиотеки" id="design-system-used">
      <FixtureLibsUsed
        items={[
          { label: "React", href: "https://react.dev/" },
          {
            label: "Next.js (Link)",
            href: "https://nextjs.org/docs/app/api-reference/components/link",
          },
          { label: "next-intl", href: "https://next-intl-docs.vercel.app/" },
          { label: "shadcn/ui — Card", href: "https://ui.shadcn.com/docs/components/card" },
          { label: "Tailwind CSS", href: "https://tailwindcss.com/docs" },
          { label: "clsx", href: "https://github.com/lukeed/clsx" },
        ]}
      />
    </FixtureSection>

    {/* 9. Рекомендации */}
    <FixtureSection title="9. Рекомендации" id="guidelines">
      <ul className="ml-6 list-disc space-y-1">
        <li>
          Передавайте валидный <code>uiLinks.edit</code> для кликабельной кнопки действия.
        </li>
        <li>Следите за корректностью статусов workflow, от них зависит режим действия.</li>
        <li>Для длинных заголовков опирайтесь на контейнерные запросы и ограничения ширины.</li>
        <li>Брендинг события опционален; отсутствие логотипа безопасно.</li>
      </ul>
    </FixtureSection>

    {/* 10. Бизнес‑правила */}
    <FixtureSection title="10. Бизнес‑правила" id="business-rules">
      <div className="space-y-3">
        <ul className="ml-6 list-disc space-y-1">
          <li>
            Инвариант 1 (минимальный): если <code>activity.content.data.title</code> отсутствует,
            компонент возвращает <code>null</code> и не занимает место в сетке.
          </li>
          <li>
            Инвариант 2 (приоритет изображений): компонент извлекает OG-изображения через{" "}
            <code>proposalsTools.activityTools.extractOGImagesUrls()</code> с параметром{" "}
            <code>deleteDefaultOg: true</code>. Если для текущей локали есть OG-изображение, оно
            отображается с абсолютным позиционированием (<code>absolute inset-0</code>),
            растягивается на весь контейнер (<code>h-full w-full object-cover</code>) и скрывает
            фоновый паттерн контейнера. Если OG отсутствует — фоллбэк на{" "}
            <code>event.content.data.logoUri.iv</code> с центрированием.
          </li>
          <li>
            Инвариант 3 (локализация): заголовок, роли и статусы берутся через i18n‑утилиты
            <code>useTranslateProposalFields</code>; ключ описания статуса — суффикс
            <code>_description</code>. Текущая локаль определяется через <code>useLocale()</code>{" "}
            для выбора OG-изображения.
          </li>
          <li>
            Инвариант 3.1 (роль участника): если <code>proposal.member.role</code> указана, бейдж
            MemberRoleBadge отображается справа от типа активности с подсказкой при наведении.
          </li>
          <li>
            Инвариант 4 (scope): бейдж видимости выводится только при <code>scope !== REGULAR</code>
            .
          </li>
          <li>
            Инвариант 5 (навигация): если <code>isClosedProposal(proposal) === true</code>, нижняя
            кнопка переключается в режим «view», иначе — «edit».
          </li>
          <li>
            Инвариант 6 (ссылки события): ссылка на сайт события и его название опциональны; при
            отсутствии данных блок аккуратно деградирует.
          </li>
          <li>
            Инвариант 7 (возврат): параметр возврата в URL явно отключён (
            <code>disableReturnInUrl</code>) для предотвращения засорения истории.
          </li>
          <li>
            Инвариант 8 (адаптив): типографика заголовка масштабируется через container queries
            классы <code>@[200px]/card</code>, <code>@[250px]/card</code>.
          </li>
          <li>
            Инвариант 9 (визуальная чистота OG): при наличии OG-изображения фоновый цвет контейнера
            (<code>bg-muted/50</code>) и паттерн-оверлей скрываются для предотвращения визуальных
            артефактов (двойная рамка). Alt текст для OG использует заголовок активности.
          </li>
        </ul>
      </div>
    </FixtureSection>

    {/* 11. Примечания/допущения */}
    <FixtureSection title="11. Примечания/допущения" id="notes-assumptions">
      <div className="space-y-3">
        <p>
          <strong>Контекстуальные допущения:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-2">
          <li>
            Логика закрытой заявки: <code>proposalsTools.filters.isClosedProposal()</code> корректно
            определяет закрытые статусы.
          </li>
          <li>
            Обработка прав: <code>rights.canPatch</code> игнорируется ссылкой действия (права
            проверяются на уровне роутинга).
          </li>
          <li>Локализация: все статусы имеют переводы с суффиксом _description.</li>
          <li>Container queries: поддержка включена в Tailwind конфигурации.</li>
          <li>Параметр возврата: отключён намеренно для предотвращения засорения истории.</li>
          <li>
            OG-изображения: извлекаются через{" "}
            <code>proposalsTools.activityTools.extractOGImagesUrls()</code>, размер 800×600 для
            оптимального качества, позиционирование <code>absolute inset-0</code> для покрытия всей
            ширины контейнера.
          </li>
          <li>
            Приоритет изображений: OG для текущей локали → логотип события → пустой фон. При наличии
            OG alt текст использует заголовок активности.
          </li>
        </ul>
        <p>
          <strong>TODO/риски:</strong>
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Подтвердить наличие переводов для всех 11 статусов workflow.</li>
          <li>Проверить контрастность WCAG AA для бейджей в тёмной теме.</li>
          <li>Уточнить поведение при отсутствии/невалидности uiLinks.edit.</li>
          <li>
            Проверить корректность отображения OG-изображений различных соотношений сторон с{" "}
            <code>object-cover</code>.
          </li>
        </ul>
      </div>
    </FixtureSection>
  </>
);

export default ProposalCardFixture;

export const meta: DesignSystemFixtureMetadata = {
  group: "Proposals",
  parentItem: "Cards",
  title: "Proposal Card",
  description: "Карточка заявки: заголовок, статус, бренд события и действия (edit/view)",
};
